import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import api from "../../lib/api";
import {
  LayoutDashboard,
  ListChecks,
  Type,
  Settings as Cog,
  Sparkles as SparkleIcon,
  LogOut,
  Sparkles,
  GraduationCap,
  MousePointerClick,
  CreditCard,
  Mail,
  Award,
  Trophy,
  FlaskConical,
  Activity,
  DatabaseBackup,
  MonitorPlay,
  SlidersHorizontal,
  FileText,
  Archive,
  KeyRound,
  Menu,
  X,
} from "lucide-react";

export default function AdminLayout() {
  const { admin, setAdmin } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (admin === null) return <div className="p-10">Loading...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    // Respond instantly: clear local auth and navigate first, then tell the
    // server in the background. Awaiting the API could hang the button if the
    // request is slow/unreachable.
    localStorage.removeItem("admin_token");
    setAdmin(false);
    navigate("/admin/login", { replace: true });
    api.post("/auth/logout").catch(() => {});
  };

  const nav = [
    { section: "Geral" },
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/metrics", label: "Health & Métricas", icon: Activity },
    { section: "Concurso" },
    { to: "/admin/quizzes", label: "Quizzes", icon: ListChecks },
    { to: "/admin/teacher-password", label: "Senha de professor", icon: KeyRound },
    { to: "/admin/archive", label: "Arquivo de perguntas", icon: Archive },
    { to: "/admin/enrollments", label: "Inscrições & Pagamentos", icon: GraduationCap },
    { to: "/admin/forms", label: "Formulários", icon: FileText },
    { to: "/admin/diplomas", label: "Diplomas", icon: Award },
    { to: "/admin/rankings", label: "Classificações", icon: Trophy },
    { section: "Aparência" },
    { to: "/admin/main-settings", label: "Definições principal", icon: SlidersHorizontal },
    { to: "/admin/settings-x", label: "Outras definições", icon: Cog },
    { to: "/admin/effects", label: "Efeitos", icon: SparkleIcon },
    { to: "/admin/quiz-appearance", label: "Aparência do Quiz", icon: MonitorPlay },
    { to: "/admin/texts", label: "Textos (i18n)", icon: Type },
    { section: "Comunicação" },
    { to: "/admin/emails", label: "Emails", icon: Mail },
    { section: "Ferramentas" },
    { to: "/admin/test-zone", label: "Zona de Testes", icon: FlaskConical },
    { to: "/admin/migration", label: "Migração", icon: DatabaseBackup },
  ];

  return (
    <div className="min-h-screen md:flex bg-[#F8FAFC] font-manrope" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[#0B1D3A] text-white px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FF6B6B]">
            <Sparkles size={18} strokeWidth={2.5} />
          </span>
          <p className="font-outfit font-bold text-sm">NATALMÁTICO <span className="opacity-60 font-normal">Back Office</span></p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="admin-menu-open" aria-label="Abrir menu" className="p-1.5 rounded-lg hover:bg-white/10">
          <Menu size={24} />
        </button>
      </div>

      {/* Mobile overlay */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} data-testid="admin-sidebar-overlay" />
      )}

      <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 shrink-0 bg-[#0B1D3A] text-white flex flex-col overflow-y-auto transform transition-transform duration-300 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-5 py-6 flex items-center gap-3 border-b border-white/10">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF6B6B]">
            <Sparkles size={20} strokeWidth={2.5} />
          </span>
          <div className="flex-1">
            <p className="font-outfit font-bold">NATALMÁTICO</p>
            <p className="text-xs opacity-60">Back Office</p>
          </div>
          <button onClick={() => setOpen(false)} data-testid="admin-menu-close" aria-label="Fechar menu" className="md:hidden p-1.5 rounded-lg hover:bg-white/10">
            <X size={20} />
          </button>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {nav.map((n, i) =>
            n.section ? (
              <p key={`sec-${i}`} className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/35 first:pt-1">
                {n.section}
              </p>
            ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition ${
                  isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"
                }`
              }
            >
              <n.icon size={18} />
              {n.label}
            </NavLink>
            )
          )}
        </nav>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/5 font-medium"
        >
          <LogOut size={18} /> Sair
        </button>
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-8 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
